import { useState } from 'react';
import { ShieldAlert, ShieldQuestion, Loader as Loader2 } from 'lucide-react';
import { Sentry } from '../../../lib/sentry';
import { useApi } from '../../../lib/useApi';
import { useAuth } from '../../../contexts/AuthContext';
import type { Account } from '../../../types';

interface Props {
  account: Account;
  isOwner: boolean;
  enriching: boolean;
  onAccountUpdate: (account: Account) => void;
  // Takes the corrected account: the parent's copy is a render behind at this point, so the
  // re-run has to be handed the row we just saved.
  onRerun: (corrected: Account) => void;
}

// Surfaces the outcome of the identity gate that runs before enrichment, and lets the user
// correct the inputs it keys off -- or adopt the organisation it found instead. Two states:
//   blocked   -- the gate refused (needs_review): the site resolved to a different company,
//                or its operator could not be established. No dossier was written.
//   uncertain -- a dossier exists but the identity behind it was never confirmed (the gate
//                could not run, or the research came back low-confidence).
// Until this existed, identity_confidence was written on every enrichment and read by
// nobody: a wrong-company dossier looked exactly like a correct one.
export default function IdentityAlert({ account, isOwner, enriching, onAccountUpdate, onRerun }: Props) {
  const { apiPut } = useApi();
  const { organisation } = useAuth();
  const [editing, setEditing] = useState(false);
  const [companyName, setCompanyName] = useState(account.company_name || '');
  const [website, setWebsite] = useState(account.website_url || '');
  const [ticker, setTicker] = useState(account.ticker_symbol || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const blocked = account.enrichment_status === 'needs_review';
  const uncertain = account.enrichment_status === 'completed'
    && account.identity_confidence !== 'verified';

  if (enriching || (!blocked && !uncertain)) return null;

  // The gate often names the right organisation itself (the group behind a legal entity's
  // name), and the user should be able to take that answer rather than retype it.
  const suggestedName = blocked ? account.identity_suggested_name?.trim() || '' : '';
  const suggestedDomain = account.identity_suggested_domain?.trim() || '';

  const saveAndRerun = async (changes: Partial<Pick<Account, 'company_name' | 'website_url' | 'ticker_symbol'>>) => {
    if (!organisation) return;
    setSaving(true);
    setError('');

    try {
      const { account: updated } = await apiPut<{ account: Account }>(
        `/v1/account/${account.id}?org_id=${encodeURIComponent(organisation.id)}`,
        changes,
      );
      onAccountUpdate(updated);
      setEditing(false);
      // The corrected name and URL are what the gate checks against, so re-verification has to be a
      // forced run: a cached/company-linked profile would otherwise short-circuit it.
      onRerun(updated);
    } catch (err) {
      Sentry.captureException(err, { tags: { context: 'identity_correction' } });
      setError(err instanceof Error ? err.message : 'Failed to update the account');
    } finally {
      setSaving(false);
    }
  };

  const tone = blocked
    ? { box: 'bg-app-warning/10 border-app-warning', title: 'text-app-warning', body: 'text-app-warning', hint: 'text-app-warning', icon: 'text-app-warning' }
    : { box: 'bg-app-canvas border-app-line-strong', title: 'text-app-ink', body: 'text-app-muted', hint: 'text-app-muted', icon: 'text-app-muted' };

  const Icon = blocked ? ShieldAlert : ShieldQuestion;

  return (
    <div className={`mb-4 p-4 border rounded-lg ${tone.box}`}>
      <div className="flex items-start gap-3">
        <Icon className={`w-5 h-5 flex-shrink-0 mt-0.5 ${tone.icon}`} />
        <div className="flex-1">
          <p className={`text-sm font-semibold ${tone.title}`}>
            {blocked ? 'Company Identity Not Verified' : 'Company identity not confirmed'}
          </p>
          <p className={`text-sm mt-1 ${tone.body}`}>
            {blocked
              ? (account.enrichment_error || 'The system could not confirm that the research data matches this company. The name may be shared by other organisations.')
              : 'This profile was generated without a confirmed match between the company name and its website, so it may describe a different organisation with a similar name.'}
          </p>

          {isOwner && !editing && suggestedName && (
            <div className="mt-3">
              <button
                onClick={() => saveAndRerun({
                  company_name: suggestedName,
                  ...(suggestedDomain ? { website_url: `https://${suggestedDomain}` } : {}),
                })}
                disabled={saving}
                className="btn-primary !py-1.5 !px-3 text-xs inline-flex items-center gap-2"
              >
                {saving && <Loader2 className="w-3 h-3 animate-spin" />}
                Use “{suggestedName}”{suggestedDomain ? ` (${suggestedDomain})` : ''} and re-verify
              </button>
              {error && <p className="text-xs text-app-critical mt-2">{error}</p>}
            </div>
          )}

          {isOwner && !editing && (
            <button
              onClick={() => setEditing(true)}
              className={`text-xs font-medium mt-2 underline underline-offset-2 ${tone.hint}`}
            >
              Not the right company? Correct the name or website and re-verify
            </button>
          )}

          {isOwner && editing && (
            <div className="mt-3 space-y-2">
              <input
                type="text"
                value={companyName}
                onChange={e => setCompanyName(e.target.value)}
                placeholder="Company name"
                className="input-field !py-2 w-full"
              />
              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  type="url"
                  value={website}
                  onChange={e => setWebsite(e.target.value)}
                  placeholder="https://company.com"
                  className="input-field !py-2 flex-1"
                />
                <input
                  type="text"
                  value={ticker}
                  onChange={e => setTicker(e.target.value)}
                  placeholder="Ticker (optional)"
                  className="input-field !py-2 sm:w-40"
                />
              </div>
              <p className={`text-xs ${tone.hint}`}>
                The website identifies the company. A ticker symbol helps for listed organisations.
              </p>
              {error && <p className="text-xs text-app-critical">{error}</p>}
              <div className="flex gap-2">
                <button
                  onClick={() => saveAndRerun({
                    company_name: companyName.trim(),
                    website_url: website.trim(),
                    ticker_symbol: ticker.trim(),
                  })}
                  disabled={saving || !companyName.trim() || !website.trim()}
                  className="btn-primary !py-1.5 !px-3 text-xs inline-flex items-center gap-2"
                >
                  {saving && <Loader2 className="w-3 h-3 animate-spin" />}
                  Save and re-verify
                </button>
                <button
                  onClick={() => { setEditing(false); setError(''); }}
                  className="btn-ghost !py-1.5 !px-3 text-xs"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
